import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useDeleteResidence } from '../../../hooks/mutations/useDeleteResidence';
import { useUpdateCaregiverRelationship } from '../../../hooks/mutations/useUpdateCaregiverRelationship';
import { useUpdateDependantPartnership } from '../../../hooks/mutations/useUpdateDependantPartnership';
import { useUpsertResidence } from '../../../hooks/mutations/useUpsertResidence';
import { useDependantResidences } from '../../../hooks/queries/useDependantResidences';
import { useDependants } from '../../../hooks/queries/useDependants';
import { getOptionalEnv } from '../../../lib/getEnv';

import ResidenceArrangementPage from './residence/ResidenceArrangementPage';
import ResidenceDetailsPage from './residence/ResidenceDetailsPage';
import {
  buildCaregiverRelationshipUpdates,
  buildFrequentPlaceInputs,
  buildPrimaryResidenceInputs,
  computeOrphanResidenceIds,
  isArrangementReady,
  isDetailsReady,
  shouldStartOnDetails,
} from './residence/residenceDraft';
import { useResidenceDraft } from './residence/useResidenceDraft';

import type { IOnboardingStepProps } from '../types';

type ResidencePage = 'arrangement' | 'details';

/**
 * "Living situation" — the loved-one residence section, presented as two
 * sequential pages behind a single wizard screen:
 *
 *   1. **arrangement** — living-arrangement toggles (live together? / live
 *      with you?). Writes only the in-progress draft.
 *   2. **details** — addresses for anyone not living with the caregiver, plus
 *      one shared "spend time elsewhere?" question. Continue writes the
 *      section's rows and advances only if every write succeeds (the writes are
 *      independent — see `handleSubmit` for the partial-failure handling).
 *
 * Modeling the two pages as internal state (not two wizard screens) keeps the
 * draft local — no provider, no screen-order/derive changes — and means "Skip
 * for now" (on both pages) is the ordinary `onNext` advancing past the whole
 * section, and Back between pages is a plain `setPage`. Nothing is written until
 * the details Continue, so the `hasDependantResidenceInfo` resume signal can't
 * flip until then: a user who abandons mid-section restarts on the arrangement
 * page, while one who already completed it resumes on the details page (see
 * {@link shouldStartOnDetails}).
 *
 * Persistence creates 0..N residences (1 primary per "live with you = No"
 * group; 1 non-primary per frequent location), updates the caregiver-edge
 * `livesWithCaregiver` flags, and (for a partnership) the `liveTogether` flag.
 *
 * Figma: Mantle - UX/UI, node 4174:70736 "Onboarding Step 11".
 */
const DependantResidenceStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { dependants, partnershipExists, partnership } = useDependants();
  const { dependantResidences, refetch: refetchDependantResidences } = useDependantResidences();
  const [upsertResidence] = useUpsertResidence();
  const [deleteResidence] = useDeleteResidence();
  const [updateCaregiverRelationship] = useUpdateCaregiverRelationship();
  const [updateDependantPartnership] = useUpdateDependantPartnership();
  // Empty key falls back to the typeahead's "Could not load suggestions" empty
  // state so Storybook / key-less local dev still render.
  const apiKey = getOptionalEnv('VITE_GOOGLE_PLACES_API_KEY') ?? '';

  const { draft, setLiveTogether, setParentAForm, setParentBForm, setFrequentLocations, hydratedResidenceIdsRef } =
    useResidenceDraft(dependants, dependantResidences, partnership);

  const isPartnership = partnershipExists && dependants.length >= 2;

  const [page, setPage] = React.useState<ResidencePage | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(false);

  // Decide the entry page once, after the dependant data resolves: a returning
  // user whose residence section is already saved opens on the details page
  // rather than re-answering the arrangement toggles. A one-shot effect (not a
  // render-time initializer) so it can't run before the data is warm and never
  // overrides a later Back to the arrangement page. The answers live on the
  // dependant / partnership rows, so this is correct even before the lazily
  // fetched residence-address query resolves.
  const entryPageResolvedRef = React.useRef(false);
  React.useEffect(() => {
    if (entryPageResolvedRef.current || dependants.length === 0) {
      return;
    }
    entryPageResolvedRef.current = true;
    setPage(
      shouldStartOnDetails(dependants, dependantResidences, partnership, isPartnership) ? 'details' : 'arrangement'
    );
  }, [dependants, dependantResidences, partnership, isPartnership]);

  const arrangementReady = isArrangementReady(draft, isPartnership);
  const detailsReady = isDetailsReady(draft, isPartnership);

  const handleSubmit = React.useCallback(async () => {
    if (!detailsReady || isSubmitting) {
      return;
    }
    const inputs = [
      ...buildPrimaryResidenceInputs(draft, dependants, isPartnership),
      ...buildFrequentPlaceInputs(draft.frequentLocations),
    ];
    const orphanIds = computeOrphanResidenceIds(inputs, hydratedResidenceIdsRef.current);
    const caregiverRelUpdates = buildCaregiverRelationshipUpdates(draft, dependants, isPartnership);

    setIsSubmitting(true);
    setSubmitError(false);
    try {
      // `allSettled`, not `all`: each upsert commits independently server-side,
      // so one rejection must not strand the rows that succeeded. We always
      // refetch (so the cache reflects partial success) and only advance when
      // every operation succeeded. A retry is safe — the server routes a
      // primary re-create to an update, so it can't trip the per-owner index.
      const results = await Promise.allSettled([
        ...inputs.map((input) => upsertResidence({ variables: { input } })),
        ...orphanIds.map((residenceId) => deleteResidence({ variables: { input: { residenceId } } })),
        ...caregiverRelUpdates.map((input) => updateCaregiverRelationship({ variables: { input } })),
        ...(isPartnership && partnership !== null && draft.liveTogether !== null
          ? [
              updateDependantPartnership({
                variables: { input: { dependantPartnershipId: partnership.id, liveTogether: draft.liveTogether } },
              }),
            ]
          : []),
      ]);
      // Refresh the server-derived residence fields into the cache, so a
      // back-nav to this section re-reads fresh data rather than the
      // pre-mutation snapshot — even when some operations failed.
      await refetchDependantResidences();

      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (failures.length > 0) {
        failures.forEach((f) => logger.error('Failed to reconcile residences', f.reason));
        setSubmitError(true);
        return;
      }
      onNext();
    } catch (error) {
      logger.error('Failed to reconcile residences', error);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    detailsReady,
    isSubmitting,
    draft,
    dependants,
    isPartnership,
    partnership,
    hydratedResidenceIdsRef,
    upsertResidence,
    deleteResidence,
    updateCaregiverRelationship,
    updateDependantPartnership,
    refetchDependantResidences,
    onNext,
  ]);

  // Render nothing until the entry-page effect has settled `page` (one frame on
  // the warm path; longer on a cold load until `useDependants` resolves).
  if (dependants.length === 0 || page === null) {
    return null;
  }

  if (page === 'arrangement') {
    return (
      <ResidenceArrangementPage
        dependants={dependants}
        isPartnership={isPartnership}
        draft={draft}
        onLiveTogetherChange={setLiveTogether}
        onParentAFormChange={setParentAForm}
        onParentBFormChange={setParentBForm}
        isContinueDisabled={!arrangementReady}
        onContinue={() => setPage('details')}
        onBack={onBack}
        onSkip={onNext}
      />
    );
  }

  const dependantAPrimary = dependantResidences.find((d) => d.dependantId === dependants[0].id)?.primary ?? null;

  return (
    <ResidenceDetailsPage
      dependants={dependants}
      isPartnership={isPartnership}
      draft={draft}
      dependantAPrimary={dependantAPrimary}
      apiKey={apiKey}
      onParentAFormChange={setParentAForm}
      onParentBFormChange={setParentBForm}
      onFrequentLocationsChange={setFrequentLocations}
      isContinueDisabled={!detailsReady || isSubmitting}
      isContinueLoading={isSubmitting}
      submitError={submitError}
      onContinue={() => void handleSubmit()}
      onBack={() => setPage('arrangement')}
      onSkip={onNext}
    />
  );
};

DependantResidenceStep.displayName = 'DependantResidenceStep';

export default DependantResidenceStep;
